import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  Lock,
  PlayCircle,
  Loader2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import SchoolStudentArena from "../components/SchoolStudentArena";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { api, type ModuleAssignment, type Module, type Lesson } from "../lib/api";

/** Shows a single module's lessons when ?uuid= param is present */
function ModuleDetailView({ moduleId }: { moduleId: string }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mod, setMod] = useState<Module | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadModule();
  }, [moduleId]);

  const loadModule = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getModuleById(moduleId);
      if (!data) {
        setError("Module not found");
        return;
      }
      setMod(data);
    } catch (err) {
      console.error("Failed to load module:", err);
      setError(err instanceof Error ? err.message : "Failed to load module");
    } finally {
      setLoading(false);
    }
  };

  const handleLessonClick = (lesson: Lesson) => {
    if (!user) {
      navigate("/login");
      return;
    }
    const status = lesson.progress?.status || "locked";
    if (status === "locked") return;
    navigate(`/lesson/${lesson.id}?returnTo=/arena`);
  };

  const getLessonStatusIcon = (lesson: Lesson) => {
    const status = lesson.progress?.status || "locked";
    switch (status) {
      case "completed":
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case "in_progress":
        return <PlayCircle className="w-5 h-5 text-blue-500" />;
      case "unlocked":
        return <Circle className="w-5 h-5 text-gray-400" />;
      default:
        return <Lock className="w-5 h-5 text-gray-300" />;
    }
  };

  const getLessonStatusColor = (lesson: Lesson) => {
    const status = lesson.progress?.status || "locked";
    switch (status) {
      case "completed":
        return "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30";
      case "in_progress":
        return "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30";
      case "unlocked":
        return "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800";
      default:
        return "border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !mod) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          {error || "Module not found"}
        </p>
        <Button variant="outline" onClick={() => navigate("/arena")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Arena
        </Button>
      </div>
    );
  }

  const lessons = mod.lessons || [];
  const completedCount = lessons.filter(
    (l) => l.progress?.status === "completed",
  ).length;

  return (
    <div>
      <Button
        variant="ghost"
        className="mb-6"
        onClick={() => navigate("/arena")}
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Arena
      </Button>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
          {mod.name}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-2">
          {mod.description}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {completedCount} of {lessons.length} lessons completed
        </p>
      </div>

      <div className="space-y-3">
        {lessons.map((lesson) => {
          const isLocked =
            lesson.progress?.status === "locked" || !lesson.progress;
          const isClickable = !isLocked && !!user;

          return (
            <div
              key={lesson.id}
              className={`
                border-2 rounded-lg p-4 transition-all duration-300
                ${getLessonStatusColor(lesson)}
                ${isClickable ? "cursor-pointer hover:shadow-md hover:scale-[1.01]" : "cursor-not-allowed opacity-75"}
              `}
              onClick={() => handleLessonClick(lesson)}
            >
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  {getLessonStatusIcon(lesson)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-sm text-gray-900 dark:text-white">
                      {lesson.title}
                    </h4>
                    {lesson.lesson_type && (
                      <Badge variant="outline" className="text-xs capitalize">
                        {lesson.lesson_type}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-1">
                    {lesson.description}
                  </p>
                  {lesson.progress?.status === "in_progress" &&
                    lesson.progress.problems_attempted > 0 && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                        <span>
                          {lesson.progress.problems_correct}/
                          {lesson.progress.problems_attempted} correct
                        </span>
                        <span>&middot;</span>
                        <span>
                          {lesson.progress.current_accuracy?.toFixed(0)}%
                          accuracy
                        </span>
                      </div>
                    )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * School student Arena page.
 * - No ?uuid= param: shows assigned module tiles (SchoolStudentArena)
 * - ?uuid=<module_id>: shows that module's lessons (ModuleDetailView)
 */
export default function SchoolArena() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [assignedModules, setAssignedModules] = useState<ModuleAssignment[]>([]);

  const moduleUuid = searchParams.get("uuid");

  useEffect(() => {
    if (moduleUuid) return;
    loadAssignedModules();
  }, [moduleUuid]);

  const loadAssignedModules = async () => {
    try {
      const data = await api.getAssignedModules();
      setAssignedModules(data.modules || []);
    } catch (error) {
      console.error("Failed to load assigned modules:", error);
    } finally {
      setLoading(false);
    }
  };

  // Module detail view
  if (moduleUuid) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
        <div className="container mx-auto px-4 max-w-3xl">
          <ModuleDetailView moduleId={moduleUuid} />
        </div>
      </div>
    );
  }

  // Module tiles view
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
      <div className="container mx-auto px-4">
        <div className="mb-12 text-center">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            The Arena
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Your assigned modules and lessons
          </p>
        </div>

        <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Welcome{user ? `, ${user.first_name || user.username || user.email.split("@")[0]}` : ""}!
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Click on a module to see your lessons and start practicing.
          </p>
        </div>

        <SchoolStudentArena modules={assignedModules} loading={loading} />
      </div>
    </div>
  );
}
